import React, { useEffect, useState } from 'react';
import { Headphones, X, PlaneTakeoff, Repeat, CalendarCheck, Ban } from 'lucide-react';

const PHONE_DISPLAY = '+1 (855) 895-8503';
const PHONE_TEL = '+18558958503';

const services = [
  { icon: CalendarCheck, text: 'Flight Booking' },
  { icon: Repeat, text: 'Seat Change' },
  { icon: PlaneTakeoff, text: 'Easy Change Flight' },
  { icon: Ban, text: 'Easy Cancellation' },
];

// Popup is only allowed 7:00 AM - 8:00 PM America/Chicago, every day of the week.
const isWithinCallWindow = () => {
  const hour = parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      hour: 'numeric',
      hour12: false,
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'hour').value,
    10
  ) % 24;

  return hour >= 7 && hour < 20;
};

export default function CallReservationPopup() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) return;
    if (!isWithinCallWindow()) return;

    const timer = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!show) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [show]);

  if (!show) return null;

  const handleClose = () => setShow(false);
  const telHref = `tel:${PHONE_TEL}`;

  return (
    <div className="md:hidden fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      <div
        className="call-popup-anim relative flex h-full w-full flex-col justify-center overflow-y-auto bg-cover bg-center text-white"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.55)), url('/assets/img/plane.webp')",
        }}
      >
        <button
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-6 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/30"
        >
          <X size={20} />
        </button>

        <a href={telHref} onClick={handleClose} className="flex flex-col justify-center px-6 py-10 text-white no-underline">
          <div className="mx-auto mb-4 flex items-center gap-2 rounded-full border border-white/50 px-3.5 py-1.5 text-sm font-semibold">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
            </span>
            Live Agent Available
          </div>

          <div className="mb-6 text-center">
            <h4 className="mb-2 text-xl font-bold">24/7 Airlines Support</h4>
            <p className="mb-0 text-sm text-white/90">Airlines Booking &amp; Last Minute Flights</p>
          </div>

          <div className="mb-6 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-700 p-4 text-center shadow-lg">
            <div className="flex items-center justify-center gap-2">
              <Headphones size={22} />
              <span className="text-2xl font-bold">{PHONE_DISPLAY}</span>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-3">
            {services.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg bg-white/15 px-3 py-2.5 text-sm backdrop-blur">
                <Icon size={18} />
                <span>{text}</span>
              </div>
            ))}
          </div>

          <button className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 py-3 font-bold text-navy-950 shadow-lg">
            <Headphones size={18} />
            Call Now for Assistance
          </button>
        </a>
      </div>

      <style>{`
        .call-popup-anim {
          animation: callPopupIn 0.3s ease-out;
        }
        @keyframes callPopupIn {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
